import React from "react";
import { MdArrowForward } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useTable } from "react-table";
import tw from "tailwind-styled-components";

const PerformanceTable = ({ staffSummary = [] }) => {
    const navigate = useNavigate();

    const data = Array.isArray(staffSummary) ? staffSummary : [];

    const columns = React.useMemo(
        () => [
            {
                Header: "Name",
                accessor: "staffName",
                Cell: ({ row }) => (
                    <div className="flex items-center gap-3">
                        <div className="h-7 w-7 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-normal text-sm">
                            {(row.original.staffName || "N")[0]}
                        </div>

                        <div>
                            <div className="font-medium text-gray-900">
                                {row.original.staffName}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                Header: "Leads",
                accessor: "totalLeads",
                headerClassName: "text-center",
                className: "text-center",
                Cell: ({ row }) => (
                    <div>
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                            {row.original.totalLeads || 0}
                        </div>
                    </div>
                ),
            },
            {
                Header: "Follow Ups",
                accessor: "pendingFollowUps",
                headerClassName: "text-center",
                className: "text-center",
                Cell: ({ row }) => (
                    <div>
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                            {row.original.pendingFollowUps || 0}
                        </div>
                    </div>
                ),
            },
            {
                Header: "Visits",
                accessor: "pendingVisits",
                headerClassName: "text-center",
                className: "text-center",
                Cell: ({ row }) => (
                    <div>
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                            {row.original.pendingVisits || 0}
                        </div>
                    </div>
                ),
            },
            {
                Header: "Bookings",
                accessor: "totalBookings",
                headerClassName: "text-center",
                className: "text-center",
                Cell: ({ row }) => (
                    <div>
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                            {row.original.totalBookings || 0}
                        </div>
                    </div>
                ),
            },
            {
                Header: "Conv %",
                accessor: "convertedLeads",
                headerClassName: "text-center",
                className: "text-center",
                Cell: ({ row }) => {
                    const conversionRate = row.original.convertedLeads || 0;
                    const totalLeads = row.original.totalLeads || 0;
                    const percentage =
                        totalLeads > 0
                            ? Math.round((conversionRate / totalLeads) * 100)
                            : 0;

                    const badge =
                        percentage >= 60
                            ? "bg-emerald-50 text-emerald-700"
                            : percentage >= 30
                                ? "bg-amber-50 text-amber-700"
                                : "bg-rose-50 text-rose-700";

                    return (
                        <span
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${badge}`}
                        >
                            {percentage}%
                        </span>
                    );
                },
            },
        ],
        []
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useTable({
            columns,
            data,
        });

    return (
        <Container>
            {/* Header */}
            <Header>
                <Title>Team Performance</Title>
                {/* Uncomment if you want to add navigation */}
                {/* <ViewAll onClick={() => navigate("/finance/collections")}>
                    View all <MdArrowForward className="ml-1" />
                </ViewAll> */}
            </Header>

            {/* Table */}
            <TableWrapper>
                <Table {...getTableProps()}>
                    <Thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                {headerGroup.headers.map((column) => (
                                    <Th {...column.getHeaderProps()}
                                        className={column.headerClassName}
                                        key={column.id}>
                                        {column.render("Header")}
                                    </Th>
                                ))}
                            </tr>
                        ))}
                    </Thead>

                    <Tbody {...getTableBodyProps()}>
                        {rows.length > 0 ? (
                            rows.slice(0, 5).map((row) => {
                                prepareRow(row);
                                return (
                                    <Tr
                                        {...row.getRowProps()}
                                        key={row.id}
                                    // Uncomment if you want to add click handler
                                    // onClick={() => navigate(`/finance/collections/${row.original.id}`)}
                                    >
                                        {row.cells.map((cell) => (
                                            <Td {...cell.getCellProps()} className={cell.column.className} key={cell.column.id}>
                                                {cell.render("Cell")}
                                            </Td>
                                        ))}
                                    </Tr>
                                );
                            })
                        ) : (
                            <tr>
                                <Td colSpan={6} className="text-center py-6 text-gray-400">
                                    No staff performance data found
                                </Td>
                            </tr>
                        )}
                    </Tbody>
                </Table>
            </TableWrapper>
        </Container>
    );
};

const Container = tw.div`
  bg-white rounded-lg p-5
`;

const Header = tw.div`
  flex items-center justify-between mb-4
`;

const Title = tw.h3`
  text-sm font-semibold text-gray-700 uppercase tracking-wide
`;

const ViewAll = tw.button`
  text-sm text-blue-600 hover:text-blue-700
  flex items-center font-medium
`;

const TableWrapper = tw.div`
  overflow-x-auto
`;

const Table = tw.table`
  min-w-full border-separate border-spacing-y-1
`;

const Thead = tw.thead`rounded-lg`;

const Th = tw.th`
  bg-gray-100 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide
`;

const Tbody = tw.tbody``;

// const Tr = tw.tr`
//   hover:bg-gray-50
//   transition-colors
// `;

// const Td = tw.td`
//   px-4 py-3 text-sm text-gray-700
// `;
const Tr = tw.tr`
  border-b
  border-gray-100
  hover:bg-sky-50
  transition-colors
`;

const Td = tw.td`
  px-6
  py-4
  text-sm
  text-gray-700
`;

export default PerformanceTable;